import {FC, useEffect, useState} from 'react';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import Input from '@/shared/components/Inputs/Input';
import FilterMenu from '@/shared/components/Table/FilterMenu';
import {BODY1, defaultPage, defaultPageSize} from '@/utils/constants';
import Modal from '@/shared/components/Modal';
import Pagination from '@/shared/components/Pagination';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {ReactComponent as DeactivateIcon} from '@icons/shield-cross.svg';
import {ReactComponent as RestoreIcon} from '@icons/shield-tick.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import FilterContent from '@/pages/Admin/Events/FilterContent';
import {eventInfoTableStyle} from './EventsInfo.style';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {IAdminEvent, IAdminOrganizationResponse, IEventStatus} from '@/utils/types/superAdmin.interface';
import {getAllOrganizationsEvents, getEventsListSelector} from '@/store/admin';
import dayjs from 'dayjs';
import {SuperAdminService} from '@/utils/services';
import {EventStatus} from '@/utils/enums';

interface TableData {
  id: number;
  name: string;
  date: {
    start: string;
    end: string;
  };
  organization?: {name: string; email: string};
  status: 'Active' | 'Blocked';
}

export interface IFilterGroup {
  title: string;
  options: IFilterOption[];
}

export interface IFilterOption {
  title: string;
  isSelected: boolean;
  isCheckBox?: boolean;
  id?: number | null;
  value?: string | null;
}

export const defaultFilterOptions: IFilterGroup[] = [
  {
    title: 'Status',
    options: [
      {title: 'All events', isSelected: true, value: 'AllEvents'},
      {title: 'Active', isSelected: false, value: 'active'},
      {title: 'Blocked', isSelected: false, value: 'blocked'},
    ],
  },
];

type EventColumn = 'id' | 'name' | 'date' | 'status';

const columnOptions: EventColumn[] = ['id', 'name', 'date', 'status'];

interface OrganizationDetailsProps {
  organization: IAdminOrganizationResponse | null;
}

const EventInfo: FC<OrganizationDetailsProps> = ({organization}) => {
  const dispatch = useAppDispatch();
  const baseURL = process.env.REACT_APP_FE_URL;

  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState<number>(defaultPage);
  // const [filterValues, setFilterValues] = useState(mockData);
  const [sortState, setSortState] = useState<{column: keyof TableData | null; direction: 'asc' | 'desc'}>({
    column: null,
    direction: 'asc',
  });
  const [filterOptions, setFilterOptions] = useState<IFilterGroup[]>(defaultFilterOptions);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<IAdminEvent | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {currentPage, pageSize, totalCount, data} = useAppSelector(getEventsListSelector) ?? {
    currentPage: 1,
    totalPages: 0,
    pageSize: defaultPageSize,
    totalCount: 0,
    data: [],
  };

  // const sortData = (column: keyof TableData) => {
  //   const direction = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

  //   const sorted = [...filterValues].sort((a, b) => {
  //     let valA = a[column];
  //     let valB = b[column];

  //     if (column === 'date') {
  //       valA = a.date.start;
  //       valB = b.date.start;
  //     }

  //     return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
  //   });
  // };

  const handleFilterChange = (groupTitle: string, optionValue: string) => {
    setPage(1);

    const updatedOptions = filterOptions.map((group) => {
      if (group.title !== groupTitle) return group;

      return {
        ...group,
        options: group.options.map((opt) => ({
          ...opt,
          isSelected: opt.value === optionValue,
        })),
      };
    });

    setFilterOptions(updatedOptions);
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const getStatusSx = (status: string) => {
    if (status.includes('Active')) return eventInfoTableStyle.statusActive;
    if (status.includes('Blocked')) return eventInfoTableStyle.statusBlocked;
    return eventInfoTableStyle.statusDefault;
  };

  const fetchEvents = async () => {
    setIsDataLoaded(false);
    const data = {
      page,
      pageSize: defaultPageSize,
      ...(searchName && {searchName}),
      status: filterOptions[0].options.find((opt) => opt.isSelected)?.value as IEventStatus,
      organizationId: organization?.id,
    };
    try {
      await dispatch(getAllOrganizationsEvents(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
    setIsDataLoaded(true);
  };

  const handleRestoreEvent = async () => {
    try {
      await SuperAdminService.updateEventStatus({
        id: selectedEvent?.id || 0,
        status: EventStatus.ACTIVE as IEventStatus,
      });
      await fetchEvents();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleBlockEvent = async () => {
    try {
      await SuperAdminService.updateEventStatus({
        id: selectedEvent?.id || 0,
        status: EventStatus.BLOCKED as IEventStatus,
      });
      await fetchEvents();
      setIsChangeStatusModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [page, searchName, filterOptions, organization?.id]);

  return (
    <Box sx={eventInfoTableStyle.wrapper}>
      <Box sx={eventInfoTableStyle.filterRow}>
        <Box>
          <FilterMenu content={<FilterContent options={filterOptions} onFilterChange={handleFilterChange} />} />
        </Box>
        <Box sx={eventInfoTableStyle.search}>
          <SearchIcon />
          <Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
        </Box>
      </Box>
      <TableContainer>
        <Table sx={eventInfoTableStyle.table} aria-label="simple table">
          <TableHead>
            <TableRow sx={eventInfoTableStyle.tableHeader}>
              {columnOptions.map((col) => {
                const sortable = col === 'id' || col === 'name' || col === 'date';

                return (
                  <TableCell
                    key={col}
                    //  onClick={() => sortable && sortData(col as keyof TableData)}
                  >
                    <Box sx={eventInfoTableStyle.headerCellTitle}>{col[0].toUpperCase() + col.slice(1)}</Box>
                  </TableCell>
                );
              })}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody sx={eventInfoTableStyle.tableBody}>
            {data.map((row, index) => (
              <TableRow key={index}>
                {columnOptions.map((col) => {
                  if (col === 'date') {
                    return (
                      <TableCell key={col}>
                        <Box>
                          <Typography>
                            <span style={eventInfoTableStyle.dateLabel}>Start: </span>
                            {`${dayjs.utc(row.dateStart).format('DD.MM.YYYY')} ${dayjs
                              .utc(row.dateStart)
                              .format('HH:mm')}`}
                          </Typography>
                          <Typography>
                            <span style={eventInfoTableStyle.dateLabel}>End: </span>
                            {`${dayjs.utc(row.dateEnd).format('DD.MM.YYYY')} ${dayjs.utc(row.dateEnd).format('HH:mm')}`}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (col === 'id') {
                    return (
                      <TableCell key={col} sx={eventInfoTableStyle.textID}>
                        {row.id}
                      </TableCell>
                    );
                  }
                  if (col === 'name') {
                    return (
                      <TableCell key={col} sx={eventInfoTableStyle.name}>
                        {row.name}
                      </TableCell>
                    );
                  }
                  if (col === 'status') {
                    return (
                      <TableCell key={col}>
                        <Typography sx={getStatusSx(row.status)}>{row.status}</Typography>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={col}>
                      <Typography>{row[col]}</Typography>
                    </TableCell>
                  );
                })}
                <TableCell sx={eventInfoTableStyle.menu}>
                  <ActionsMenu
                    content={
                      <Box>
                        <Typography
                          sx={eventInfoTableStyle.menuItem}
                          onClick={() => {
                            window.open(`${baseURL}/${row.customizeWebpage}`, '_blank', 'noopener,noreferrer');
                          }}
                          variant={BODY1}
                        >
                          Open event page
                        </Typography>
                        <Typography
                          sx={eventInfoTableStyle.menuItem}
                          onClick={() => {
                            setSelectedEvent(row);
                            setIsChangeStatusModalOpen(true);
                          }}
                          variant={BODY1}
                        >
                          {row.status === 'Active' ? 'Block event' : 'Restore event'}
                        </Typography>
                      </Box>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={eventInfoTableStyle.paginationContainer}>
        <Pagination page={page} pageSize={pageSize} total={totalCount} onChange={(step) => setPage(step)} />
      </Box>
      <Modal
        mainButtonTitle={selectedEvent?.status === 'Active' ? 'Block Event' : 'Restore Event'}
        closeButtonTitle="Cancel"
        open={isChangeStatusModalOpen}
        onClose={() => setIsChangeStatusModalOpen(false)}
        onSave={selectedEvent?.status === 'Active' ? handleBlockEvent : handleRestoreEvent}
        mainIcon={selectedEvent?.status === 'Active' ? <DeactivateIcon /> : <RestoreIcon />}
        sx={eventInfoTableStyle.modal}
        title={selectedEvent?.status === 'Active' ? 'Block Event' : 'Restore Event'}
        color={selectedEvent?.status === 'Active' ? 'error' : 'primary'}
        isShowCloseButton
        isShowSaveButton
      >
        {selectedEvent?.status === 'Active' ? (
          <Box sx={eventInfoTableStyle.textWrapper}>
            <Typography sx={eventInfoTableStyle.bold}>{selectedEvent.name} </Typography>
            <Typography sx={eventInfoTableStyle.description}>will be blocked. </Typography>
            <Typography sx={eventInfoTableStyle.bold}>{selectedEvent.name} </Typography>
            <Typography sx={eventInfoTableStyle.description}>will be notified that the event is blocked</Typography>
            <Typography sx={eventInfoTableStyle.descriptionBottom}>
              Are you sure you want to block this event?
            </Typography>
          </Box>
        ) : (
          <Box sx={eventInfoTableStyle.textWrapper}>
            <Typography sx={eventInfoTableStyle.bold}>{selectedEvent?.name} </Typography>
            <Typography sx={eventInfoTableStyle.description}>will be restored. </Typography>
            <Typography sx={eventInfoTableStyle.bold}>{selectedEvent?.name} </Typography>
            <Typography sx={eventInfoTableStyle.description}>will be notified that the event is restored</Typography>
            <Typography sx={eventInfoTableStyle.descriptionBottom}>
              Are you sure you want to restore this event?
            </Typography>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default EventInfo;
